import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownToggle,
  FancyDropdownMenuItem
} from 'dpl/components/FancyDropdown';
import Icon from 'dpl/common/components/Icon';
import Label from 'dpl/form/components/Label';
import useAssignableLitterData from 'dpl/breeder_dashboard/hooks/useAssignableLitterData';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import GenericMenu from 'dpl/path_to_payment/components/GenericMenu';
import SmartImage from 'dpl/components/SmartImage';
import BottomSheetMenu from 'dpl/common/design_system/BottomSheetMenu';

const IS_LT_MD = isLessThanBreakpoint('md');

function DogAssignmentDropdownButton({
  className,
  handleDogClick,
  handleTbdDogClick,
  isTbdDog,
  dog
}) {
  const id = isTbdDog ? 'tbd' : `${dog.id}`;
  return (
    <button
      id={`dog_option_${id}`}
      data-test-id={`dog_option_${id}`}
      type="button"
      className={classnames('font-16 tl w-100', className)}
      onClick={() => (isTbdDog ? handleTbdDogClick() : handleDogClick(dog.id))}
    >
      {isTbdDog ? 'TBD' : dog.name}
    </button>
  );
}

DogAssignmentDropdownButton.propTypes = {
  className: PropTypes.string,
  dog: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }),
  handleDogClick: PropTypes.func,
  handleTbdDogClick: PropTypes.func,
  isTbdDog: PropTypes.bool
};

DogAssignmentDropdownButton.defaultProps = {
  className: null,
  dog: {},
  handleDogClick: null,
  handleTbdDogClick: null,
  isTbdDog: false
};

export function DogSelector({
  availableDogs,
  selectedDogId,
  isTbdDog,
  onDogChange,
  onTbdDogChange,
  disableTbd,
  newDesignSystemStyles
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedDog = useMemo(
    () => availableDogs.find(dog => dog.id === selectedDogId),
    [availableDogs, selectedDogId]
  );

  // New design system styles only
  const menuOptions = useMemo(() => {
    if (!newDesignSystemStyles) return [];

    const options = availableDogs.map(dog => ({
      label: dog.name,
      value: dog.id
    }));

    if (!disableTbd) {
      options.push({ label: 'TBD', value: 'tbd' });
    }

    return options;
  }, [newDesignSystemStyles, availableDogs]);

  // New design system styles only
  function handleSelect(value) {
    if (value === 'tbd') {
      onTbdDogChange();
    } else {
      onDogChange(value);
    }
  }

  return (
    <div className="DogAssignmentDropdown">
      <Label
        htmlFor="dog-assignment-dropdown"
        newDesignSystemStyles
        text="Dog"
      />
      <FancyDropdown
        id="dog-assignment-dropdown"
        onChange={setIsDropdownOpen}
        isOpen={isDropdownOpen}
        maxMenuHeight="200px"
      >
        <FancyDropdownToggle
          className="bg-white ba br3 b--default pv3 ph3 font-16"
          id="FancyDropdownToggle_DogAssignmentDropdown"
        >
          <div className="flex items-center">
            {selectedDog?.id || isTbdDog ? (
              <div className="flex items-center flex-auto">
                {selectedDog?.profile_photo_url ? (
                  <SmartImage
                    Tag="div"
                    crop
                    lazy
                    className="bg-center cover br-100 overflow-hidden"
                    wrapperOnlyClassName="flex-none"
                    src={selectedDog.profile_photo_url}
                    width="26px"
                    height="26px"
                  />
                ) : (
                  <div className="br-100 ba b--subtle-gray bg-subtle-light-gray pv1 ph1">
                    <Icon
                      className="secondary"
                      name="fetch-dog"
                      width="16px"
                      height="16px"
                    />
                  </div>
                )}
                <p className="ml2 truncate font-16">
                  {selectedDog?.name || 'TBD'}
                </p>
              </div>
            ) : (
              <span className="disabled w-100">Select a dog</span>
            )}
            <Icon
              className="secondary"
              name={`fetch-chevron-${isDropdownOpen ? 'up' : 'down'}`}
            />
          </div>
        </FancyDropdownToggle>
        {IS_LT_MD && newDesignSystemStyles && (
          <BottomSheetMenu
            isOpen={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            options={menuOptions}
            onSelect={handleSelect}
          />
        )}
        {IS_LT_MD && !newDesignSystemStyles && (
          <FixedTooltipPortal
            isVisible={isDropdownOpen}
            onCloseClick={() => setIsDropdownOpen(false)}
            style={{ maxHeight: '90vh' }}
            shouldHideCloseButton
            className="no-scrollbars"
            wrapperClassName="dn-ns"
          >
            <GenericMenu setIsMenuOpen={setIsDropdownOpen} className="ph1">
              {availableDogs.map((dog, idx) => (
                <DogAssignmentDropdownButton
                  key={idx}
                  className={classnames('pv6 bt b--subtle-gray', {
                    'bt-0': idx === 0
                  })}
                  dog={dog}
                  handleDogClick={onDogChange}
                />
              ))}
              {!disableTbd && (
                <DogAssignmentDropdownButton
                  className="pv6 bt b--subtle-gray"
                  handleTbdDogClick={onTbdDogChange}
                  isTbdDog
                />
              )}
            </GenericMenu>
          </FixedTooltipPortal>
        )}
        {!IS_LT_MD && (
          <FancyDropdownMenu
            className={classnames('DogAssignmentDropdown__menu flex-auto mt1', {
              'br3 box-shadow-2 bg-white overflow-y-auto':
                !newDesignSystemStyles
            })}
            newDesignSystemStyles={newDesignSystemStyles}
          >
            {availableDogs.map(dog => (
              <FancyDropdownMenuItem
                key={dog.id}
                itemKey={dog.id}
                className="br3 bg-white pointer"
              >
                <DogAssignmentDropdownButton
                  className="br2 ph2 pv2 hover-bg-near-white"
                  dog={dog}
                  handleDogClick={onDogChange}
                />
              </FancyDropdownMenuItem>
            ))}
            <FancyDropdownMenuItem
              key="tbd_dog"
              itemKey="tbd_dog"
              className="br3 bg-white pointer"
            >
              {!disableTbd && (
                <DogAssignmentDropdownButton
                  className="br2 ph2 pv2 hover-bg-near-white"
                  handleTbdDogClick={onTbdDogChange}
                  isTbdDog
                />
              )}
            </FancyDropdownMenuItem>
          </FancyDropdownMenu>
        )}
      </FancyDropdown>
    </div>
  );
}

DogSelector.propTypes = {
  availableDogs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      available: PropTypes.bool
    })
  ).isRequired,
  isTbdDog: PropTypes.bool,
  onDogChange: PropTypes.func.isRequired,
  onTbdDogChange: PropTypes.func.isRequired,
  selectedDogId: PropTypes.number,
  disableTbd: PropTypes.bool,
  newDesignSystemStyles: PropTypes.bool
};

DogSelector.defaultProps = {
  isTbdDog: false,
  selectedDogId: null,
  disableTbd: false,
  newDesignSystemStyles: false
};

export default function DogAssignmentDropdown({
  buyerProfileId,
  dogOptions,
  isTbdDog,
  onDogChange,
  onTbdDogChange,
  selectedDogId,
  newDesignSystemStyles
}) {
  const { assignedDog, isLoadingData } =
    useAssignableLitterData(buyerProfileId);

  const availableDogs = dogOptions.filter(
    dog => dog.available || dog.id === assignedDog?.id
  );

  useEffect(() => {
    if (!isLoadingData && !selectedDogId) {
      onDogChange(assignedDog?.id);
    }
  }, [assignedDog?.id, isLoadingData]);

  return (
    <DogSelector
      availableDogs={availableDogs}
      selectedDogId={selectedDogId}
      isTbdDog={isTbdDog}
      onDogChange={onDogChange}
      onTbdDogChange={onTbdDogChange}
      newDesignSystemStyles={newDesignSystemStyles}
    />
  );
}

DogAssignmentDropdown.propTypes = {
  buyerProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  dogOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      available: PropTypes.bool
    })
  ).isRequired,
  isTbdDog: PropTypes.bool,
  onDogChange: PropTypes.func.isRequired,
  onTbdDogChange: PropTypes.func.isRequired,
  selectedDogId: PropTypes.number,
  newDesignSystemStyles: PropTypes.bool
};

DogAssignmentDropdown.defaultProps = {
  isTbdDog: false,
  selectedDogId: null,
  newDesignSystemStyles: false
};
